import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  TrendingDown,
  Timeline,
  Speed,
  Group,
  StarRate,
  CheckCircle,
  Chat,
  AccessTime
} from '@mui/icons-material';
import { advancedReportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface AggregatedReportsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface AggregatedData {
  executive_summary: {
    total_conversations: number;
    resolution_rate: number;
    avg_response_time: number;
    customer_satisfaction: number;
    agent_utilization: number;
    period_change: {
      conversations: number;
      resolution_rate: number;
      response_time: number;
      satisfaction: number;
    };
  };
  performance_benchmarks: {
    industry_comparison: {
      resolution_rate: { current: number; industry_avg: number };
      response_time: { current: number; industry_avg: number };
      satisfaction: { current: number; industry_avg: number };
    };
    goals_tracking: {
      resolution_rate: { target: number; current: number };
      response_time: { target: number; current: number };
      satisfaction: { target: number; current: number };
    };
  };
  departmental_breakdown: Array<{
    department: string;
    agent_count: number;
    conversations_handled: number;
    avg_resolution_time: number;
    satisfaction_score: number;
    efficiency_score: number;
  }>;
  trend_analysis: {
    conversation_volume_trend: Array<{
      period: string;
      volume: number;
      growth_rate: number;
    }>;
    key_insights: string[];
    recommendations: string[];
  };
  roi_metrics: {
    cost_per_conversation: number;
    agent_productivity_score: number;
    customer_retention_impact: number;
    estimated_savings: number;
  };
}

const ExecutiveMetricCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
}> = ({ title, value, change, icon, color, suffix = '' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h3" sx={{ color, mb: 1, fontWeight: 'bold' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </Typography>
          {change !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {change > 0 ? (
                <TrendingUp sx={{ color: 'success.main', fontSize: 20, mr: 0.5 }} />
              ) : change < 0 ? (
                <TrendingDown sx={{ color: 'error.main', fontSize: 20, mr: 0.5 }} />
              ) : null}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary',
                  fontWeight: 'bold'
                }}
              >
                {change > 0 ? '+' : ''}{change.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                vs last period
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main`, width: 56, height: 56 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const BenchmarkCard: React.FC<{
  title: string;
  current: number;
  benchmark: number;
  unit: string;
  isHigherBetter?: boolean;
}> = ({ title, current, benchmark, unit, isHigherBetter = true }) => {
  const performance = isHigherBetter 
    ? current >= benchmark ? 'above' : 'below'
    : current <= benchmark ? 'above' : 'below';
  
  const percentageDiff = ((current - benchmark) / benchmark) * 100;
  
  return (
    <Paper sx={{ p: 2, bgcolor: performance === 'above' ? 'success.light' : 'warning.light' }}>
      <Typography variant="body2" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {current.toFixed(1)}{unit}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Target: {benchmark.toFixed(1)}{unit}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min((current / benchmark) * 100, 100)}
        color={performance === 'above' ? 'success' : 'warning'}
        sx={{ mb: 1 }}
      />
      <Typography 
        variant="caption" 
        color={performance === 'above' ? 'success.main' : 'warning.main'}
      >
        {percentageDiff > 0 ? '+' : ''}{percentageDiff.toFixed(1)}% vs benchmark
      </Typography>
    </Paper>
  );
};

export const AggregatedReports: React.FC<AggregatedReportsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aggregatedData, setAggregatedData] = useState<AggregatedData | null>(null);

  useEffect(() => {
    fetchAggregatedData();
  }, [dateRange]);

  const fetchAggregatedData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await advancedReportsAPI.getAggregatedMetrics(params);
      setAggregatedData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch aggregated reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Validate data structure
  const validateData = (data: any) => {
    return {
      executive_summary: data?.executive_summary || {},
      performance_benchmarks: data?.performance_benchmarks || { goals_tracking: {}, industry_comparison: {} },
      departmental_breakdown: data?.departmental_breakdown || [],
      trend_analysis: data?.trend_analysis || { conversation_volume_trend: [], key_insights: [], recommendations: [] },
      roi_metrics: data?.roi_metrics || {}
    };
  };

  const safeData = validateData(aggregatedData);

  // Safe access functions
  const getExecutiveSummary = () => safeData.executive_summary;
  const getPeriodChange = () => safeData.executive_summary?.period_change || {};
  const getPerformanceBenchmarks = () => safeData.performance_benchmarks;
  const getDepartmentalBreakdown = () => safeData.departmental_breakdown;
  const getTrendAnalysis = () => safeData.trend_analysis;
  const getROIMetrics = () => safeData.roi_metrics;

  if (!aggregatedData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No aggregated data available for the selected period.
      </Alert>
    );
  }

  // Additional validation for critical paths
  if (!safeData.executive_summary || !safeData.performance_benchmarks) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Incomplete data received from server. Some sections may not display properly.
      </Alert>
    );
  }

  // Prepare data for export
  const exportData = [
    {
      period: 'Summary',
      ...safeData.executive_summary,
      ...safeData.performance_benchmarks
    },
    ...(Array.isArray(safeData.departmental_breakdown) ? safeData.departmental_breakdown : []),
    ...(Array.isArray(safeData.trend_analysis) ? safeData.trend_analysis : [])
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Assessment sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Executive Dashboard
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="aggregated_reports"
          metadata={{ dateRange }}
        />
      </Box>
      <Box sx={{ display: 'none' }}>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Comprehensive performance overview with KPIs, benchmarks, and strategic insights
      </Typography>

      {/* Executive Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ExecutiveMetricCard
            title="Total Conversations"
            value={getExecutiveSummary()?.total_conversations || 0}
            change={getPeriodChange()?.conversations}
            icon={<Chat />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ExecutiveMetricCard
            title="Resolution Rate"
            value={(getExecutiveSummary()?.resolution_rate || 0).toFixed(1)}
            change={getPeriodChange()?.resolution_rate}
            icon={<CheckCircle />}
            color="success"
            suffix="%"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ExecutiveMetricCard
            title="Avg Response Time"
            value={(getExecutiveSummary()?.avg_response_time || 0).toFixed(1)}
            change={getPeriodChange()?.response_time}
            icon={<AccessTime />}
            color="info"
            suffix=" min"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ExecutiveMetricCard
            title="Customer Satisfaction"
            value={(getExecutiveSummary()?.customer_satisfaction || 0).toFixed(1)}
            change={getPeriodChange()?.satisfaction}
            icon={<StarRate />}
            color="warning"
            suffix="/5.0"
          />
        </Grid>
      </Grid>

      {/* Performance Benchmarks */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Speed sx={{ mr: 1, verticalAlign: 'middle' }} />
            Performance Benchmarks & Goals
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <BenchmarkCard
                title="Resolution Rate"
                current={getPerformanceBenchmarks()?.goals_tracking?.resolution_rate?.current || 0}
                benchmark={getPerformanceBenchmarks()?.goals_tracking?.resolution_rate?.target || 90}
                unit="%"
                isHigherBetter={true}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BenchmarkCard
                title="Response Time"
                current={getPerformanceBenchmarks()?.goals_tracking?.response_time?.current || 0}
                benchmark={getPerformanceBenchmarks()?.goals_tracking?.response_time?.target || 30}
                unit=" min"
                isHigherBetter={false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BenchmarkCard
                title="Customer Satisfaction"
                current={getPerformanceBenchmarks()?.goals_tracking?.satisfaction?.current || 0}
                benchmark={getPerformanceBenchmarks()?.goals_tracking?.satisfaction?.target || 4.5}
                unit="/5.0"
                isHigherBetter={true}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Departmental Performance */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
            Departmental Performance Breakdown
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Department</TableCell>
                  <TableCell align="right">Agents</TableCell>
                  <TableCell align="right">Conversations</TableCell>
                  <TableCell align="right">Avg Resolution Time</TableCell>
                  <TableCell align="right">Satisfaction</TableCell>
                  <TableCell align="right">Efficiency Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getDepartmentalBreakdown().map((dept: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {dept?.department || 'Unknown Department'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{dept?.agent_count || 0}</TableCell>
                    <TableCell align="right">{(dept?.conversations_handled || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(dept?.avg_resolution_time || 0).toFixed(1)} min</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <StarRate sx={{ color: 'warning.main', mr: 0.5, fontSize: 16 }} />
                        {(dept?.satisfaction_score || 0).toFixed(1)}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${(dept?.efficiency_score || 0).toFixed(0)}%`}
                        color={(dept?.efficiency_score || 0) >= 80 ? 'success' : (dept?.efficiency_score || 0) >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ROI & Business Impact */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ROI & Business Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Cost per Conversation</Typography>
                  <Typography variant="h6" color="primary">
                    ${(getROIMetrics()?.cost_per_conversation || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Agent Productivity Score</Typography>
                  <Typography variant="h6" color="success.main">
                    {(getROIMetrics()?.agent_productivity_score || 0).toFixed(1)}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Customer Retention Impact</Typography>
                  <Typography variant="h6" color="info.main">
                    {(getROIMetrics()?.customer_retention_impact || 0).toFixed(1)}%
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontWeight="bold">Estimated Savings</Typography>
                  <Typography variant="h5" color="success.main" fontWeight="bold">
                    ${(getROIMetrics()?.estimated_savings || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                Key Insights & Recommendations
              </Typography>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Key Insights:
              </Typography>
              <Box sx={{ mb: 2 }}>
                {(getTrendAnalysis()?.key_insights || []).map((insight: string, index: number) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                    • {insight}
                  </Typography>
                ))}
              </Box>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                Recommendations:
              </Typography>
              <Box>
                {(getTrendAnalysis()?.recommendations || []).map((recommendation: string, index: number) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                    • {recommendation}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Conversation Volume Trend */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Conversation Volume Trend Analysis
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">Growth Rate</TableCell>
                  <TableCell align="right">Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(getTrendAnalysis()?.conversation_volume_trend || []).map((trend: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{trend?.period || `Period ${index + 1}`}</TableCell>
                    <TableCell align="right">{(trend?.volume || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        color={(trend?.growth_rate || 0) >= 0 ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {(trend?.growth_rate || 0) > 0 ? '+' : ''}{(trend?.growth_rate || 0).toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {(trend?.growth_rate || 0) > 0 ? (
                        <TrendingUp color="success" />
                      ) : (trend?.growth_rate || 0) < 0 ? (
                        <TrendingDown color="error" />
                      ) : (
                        <Typography color="text.secondary">Stable</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};